import { useEffect, useRef, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d720nobk1jkj467r5p60';

export default function ContactPage() {
  const ref = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = 'Contact — HASPICA Studio de Design Bordeaux';
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll('.section-appear') ?? [];
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      data.append(key, value.toString());
    });
    if (charCount > 500) return;
    setStatus('sending');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div ref={ref}>
      <Navbar />

      {/* Header */}
      <section className="pt-40 pb-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="section-appear text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-5">Contact</p>
          <h1 className="section-appear font-heading text-5xl md:text-6xl lg:text-7xl text-neutral-900 font-light leading-tight">
            Discutons de<br />
            <em className="italic">votre projet</em>
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 pb-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">

          {/* Info */}
          <div className="section-appear lg:col-span-2 space-y-10">
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-4">Studio</p>
              <p className="text-sm text-neutral-600 leading-relaxed">
                HASPICA<br />
                Patrik Haspica<br />
                SIRET&nbsp;: 102 262 441 00011
              </p>
            </div>

            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-4">Adresse</p>
              <address className="not-italic text-sm text-neutral-600 leading-relaxed">
                12 Rue Judaïque<br />
                33000 Bordeaux<br />
                France
              </address>
            </div>

            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-4">Téléphone</p>
              <a
                href="tel:+33753495245"
                className="text-sm text-neutral-900 hover:text-neutral-600 transition-colors cursor-pointer"
              >
                +33 7 53 49 52 45
              </a>
            </div>

            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-4">Email</p>
              <a
                href="mailto:patr1khaspica@inbox.eu"
                rel="nofollow"
                className="text-sm text-neutral-900 hover:text-neutral-600 transition-colors cursor-pointer break-all"
              >
                patr1khaspica@inbox.eu
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="section-appear lg:col-span-3">
            {status === 'success' ? (
              <div className="border border-neutral-200 rounded-sm p-12 text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-neutral-400" />
                </div>
                <h3 className="font-heading text-2xl text-neutral-900 font-medium mb-3">Message envoyé</h3>
                <p className="text-sm text-neutral-500">Nous avons bien reçu votre message et vous répondrons dans les meilleurs délais.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-8 text-sm text-neutral-500 hover:text-neutral-900 transition-colors cursor-pointer underline"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-haspica"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nom" className="block text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                      Nom *
                    </label>
                    <input
                      id="nom"
                      name="nom"
                      type="text"
                      required
                      placeholder="Votre nom"
                      className="w-full border border-neutral-200 rounded-sm px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-neutral-500 transition-colors bg-white"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                      Email *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="votre@email.fr"
                      className="w-full border border-neutral-200 rounded-sm px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-neutral-500 transition-colors bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="telephone" className="block text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                    Téléphone
                  </label>
                  <input
                    id="telephone"
                    name="telephone"
                    type="tel"
                    placeholder="+33 _ __ __ __ __"
                    className="w-full border border-neutral-200 rounded-sm px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-neutral-500 transition-colors bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="projet" className="block text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                    Description du projet *
                  </label>
                  <textarea
                    id="projet"
                    name="projet"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Décrivez votre projet, vos besoins, vos délais..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-neutral-200 rounded-sm px-4 py-3 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-neutral-500 transition-colors resize-none bg-white"
                  />
                  <div className={`text-xs mt-1 text-right ${charCount > 500 ? 'text-red-500' : 'text-neutral-400'}`}>
                    {charCount}/500
                  </div>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500">Une erreur est survenue. Veuillez réessayer ou nous contacter par email.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="w-full py-4 bg-neutral-900 text-white text-sm font-medium rounded-sm hover:bg-neutral-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {status === 'sending' ? 'Envoi en cours...' : 'Envoyer le message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
